/**
 * The full-page plan editor — header, summary strip, grid, Estimates and Changes.
 *
 * Solution Detail is a drawer capped at 840px, so the grid cannot live there: 27 months at
 * 62px plus a 614px frozen block is ~2,288px of table. Hence a route of its own
 * (`/dashboard/plans/$planId`), with the Solution Detail drawer's Plans tab linking into
 * it (RP-15.1).
 */

import { Link, useRouter } from "@tanstack/react-router";
import { ChevronLeft, Pencil } from "lucide-react";
import { useState } from "react";
import { SolutionDetailDrawer } from "@/components/dashboard/SolutionDetailDrawer";
import { EstimatesPanel } from "@/components/plans/EstimatesPanel";
import {
  PlanActivityPanel,
  PlanConcernsBadge,
  RaiseConcernButton,
} from "@/components/plans/PlanActivityPanel";
import { PlanActualsPanel } from "@/components/plans/PlanActualsPanel";
import { PlanApprovalPanel } from "@/components/plans/PlanApprovalPanel";
import { PlanChangesPanel } from "@/components/plans/PlanChangesPanel";
import { PlanDatesDialog } from "@/components/plans/PlanDatesDialog";
import { PlanDatesStaffingNotice } from "@/components/plans/PlanDatesStaffingNotice";
import { PlanDealDriftWarning } from "@/components/plans/PlanDealStageBar";
import { PlanHeaderFacts } from "@/components/plans/PlanHeaderFacts";
import { PlanMoreMenu } from "@/components/plans/PlanMoreMenu";
import { PlanNosDemandSection } from "@/components/plans/PlanNosDemandSection";
import { PlannedPodPanel } from "@/components/plans/PlannedPodPanel";
import { PlanOwnerDialog } from "@/components/plans/PlanOwnerDialog";
import { PlanReturnedBanner } from "@/components/plans/PlanReturnedBanner";
import { PlanOutOfWindowNotice, PlanSetupDialog } from "@/components/plans/PlanSetupDialog";
import { PlanSolutionBar } from "@/components/plans/PlanSolutionBar";
import { PlanStaffingNotice } from "@/components/plans/PlanStaffingNotice";
import { PlanStatusControl } from "@/components/plans/PlanStatusControl";
import { FeeVarianceLine, PlanSummaryStrip } from "@/components/plans/PlanSummaryStrip";
import { PlanVersionBar } from "@/components/plans/PlanVersionBar";
import { PlanVersionsPanel } from "@/components/plans/PlanVersionsPanel";
import { PlanWorkingGrid } from "@/components/plans/PlanWorkingGrid";
import { RejectPlanButton } from "@/components/plans/RejectPlanDialog";
import { SolvePanel } from "@/components/plans/SolvePanel";
import { SubmitToResourcingButton } from "@/components/plans/SubmitToResourcingButton";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useInvalidatePlans, useResourcePlan } from "@/hooks/use-resource-plans";
import { planShowsActuals } from "@/lib/plan-actuals";
import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

type PanelTab =
  | "grid"
  | "pod"
  | "solve"
  | "versions"
  | "approval"
  | "estimates"
  | "actuals"
  | "activity"
  | "changes";

/**
 * Epic 2. The tab label carries the origin, so "Modified from a Standard Trio" is visible
 * without opening the tab — which is the point of recording `shape_source` at all.
 *
 * `plannedPod` is OPTIONAL on the view (it is an M3 addition; an older cached payload has
 * no key at all) and NULL when no Pod is composed. Both mean the same thing here.
 */
function podTabLabel(pod: ResourcePlanDetailView["plannedPod"]): string {
  return pod === null || pod === undefined ? "Planned Pod" : `Pod · ${pod.shapeSource}`;
}

/**
 * Epic 17. Same reasoning as the Pod tab: a Pending request is the thing blocking the
 * planner, and a tab that just says "Approval" hides it behind a click.
 *
 * `pendingApprovalId` and `approvalStatus` are OPTIONAL on the view (M4 additions), so an
 * older cached payload falls through to the plain label rather than rendering "undefined".
 */
function approvalTabLabel(plan: ResourcePlanDetailView): string {
  if (plan.pendingApprovalId != null) return "Approval · pending";
  if (plan.approvalStatus === "Rejected") return "Approval · rejected";
  if (plan.approvalStatus === "Approved") return "Approval · approved";
  return "Approval";
}

export function PlanEditor({ planId, initialTab }: { planId: string; initialTab?: PanelTab }) {
  const router = useRouter();
  const { data: plan, isLoading, isError, error, refetch } = useResourcePlan(planId);

  const invalidatePlans = useInvalidatePlans();

  const [tab, setTab] = useState<PanelTab>(initialTab ?? "grid");
  const [setupOpen, setSetupOpen] = useState(false);
  // RP-28: Move or Fit the plan to new dates.
  const [datesOpen, setDatesOpen] = useState(false);
  // "Edit Solution" flies the Solution Detail drawer out over the plan instead of leaving
  // it — the planner's loop is read here, change there, come straight back.
  const [solutionOpen, setSolutionOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || plan === undefined) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Could not load this Resource Plan."}
        onRetry={refetch}
      />
    );
  }

  return (
    <PageContainer>
      <button
        type="button"
        onClick={() => router.navigate({ to: "/dashboard/plans" })}
        className="inline-flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-3 w-3" />
        All plans
      </button>

      {/* ── Header ── */}
      <PageHeader
        eyebrow={
          <Link
            to="/dashboard/accounts/$id"
            params={{ id: plan.clientAccountId }}
            className="underline-offset-2 hover:underline"
          >
            {plan.clientAccountName}
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{plan.name}</span>
            {plan.kind !== "Base" && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                {plan.kind === "ChangeOrder" ? "Change Order" : plan.kind}
              </span>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PlanConcernsBadge plan={plan} onOpen={() => setTab("activity")} />
            {plan.pendingApprovalId != null && (
              <button
                type="button"
                onClick={() => setTab("approval")}
                className="rounded bg-warning-100 px-2 py-1 text-xs font-semibold text-warning-700 transition-opacity hover:opacity-80 dark:text-warning-400"
                title={
                  plan.status === "Draft"
                    ? "Finance or an Admin has to decide before this plan can be Proposed. The plan stays Open and stays editable."
                    : "A change took this plan below the margin floor or a line below cost. Finance or an Admin has to decide; the plan keeps its status."
                }
              >
                Awaiting approval
              </button>
            )}
            {plan.approvalStatus === "Rejected" && plan.pendingApprovalId == null && (
              <button
                type="button"
                onClick={() => setTab("approval")}
                className="rounded bg-error-100 px-2 py-1 text-xs font-semibold text-error-700 transition-opacity hover:opacity-80 dark:text-error-400"
                title="The last approval request was rejected. Open the Approval tab for the reason."
              >
                Approval rejected
              </button>
            )}
            {plan.marginStatus === "red" && plan.pendingApprovalId == null && (
              <span
                className="rounded bg-error-100 px-2 py-1 text-xs font-semibold text-error-700 dark:text-error-400"
                title={
                  plan.approvalStatus === "Approved"
                    ? `Below the ${plan.marginFloorPercent}% floor, and approved at that margin.`
                    : `Below the ${plan.marginFloorPercent}% floor. Proposing this plan will create an Approval Request and keep it Open until Finance or an Admin decides (RP-17.1).`
                }
              >
                {/* Once the plan is approved, "approval required" is no longer true — but the
                  plan IS still below floor, and that is worth saying on a Proposed deal. */}
                {plan.approvalStatus === "Approved"
                  ? "Below floor · approved"
                  : "Below floor · approval required"}
              </span>
            )}
            {plan.marginStatus === "amber" && (
              <span
                className="rounded bg-warning-100 px-2 py-1 text-xs font-semibold text-warning-700 dark:text-warning-400"
                title={`Above the ${plan.marginFloorPercent}% floor but inside the ${plan.marginAmberBandPoints}-point warning band. No approval needed.`}
              >
                Approaching floor
              </span>
            )}
            {(plan.canManageStatus ?? plan.canEdit) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSetupOpen(true)}
                disabled={!plan.canEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit setup
              </Button>
            )}
            {/* Reject is a Blocking Concern (RP-34); this is its findable way in. */}
            <RejectPlanButton plan={plan} />
            <RaiseConcernButton plan={plan} />
            <SubmitToResourcingButton plan={plan} onViewRejection={() => setTab("activity")} />
            <PlanStatusControl plan={plan} />
            {/* Everything used less than once per visit lives here ("Say it once"). */}
            <PlanMoreMenu plan={plan} />
          </div>
        }
      />

      <PlanHeaderFacts
        plan={plan}
        onChangeOwner={() => setOwnerDialogOpen(true)}
        onChangeDates={plan.canEdit ? () => setDatesOpen(true) : undefined}
      />

      <PlanReturnedBanner plan={plan} onViewConcern={() => setTab("activity")} />
      <PlanStaffingNotice plan={plan} />
      <PlanDatesStaffingNotice plan={plan} />

      <div className="space-y-3">
        {/*
         * Above the money, not below it: the Solution's attributes are the premises the
         * figures are an answer to, and a planner checking "is this the right tier" should
         * not have to scroll past the margin to find out.
         */}
        <PlanSolutionBar
          context={plan.solutionContext}
          onEditSolution={() => setSolutionOpen(true)}
        />
        <PlanDealDriftWarning plan={plan} />
        <PlanSummaryStrip plan={plan} />
        <FeeVarianceLine plan={plan} />
        <PlanOutOfWindowNotice plan={plan} />
      </div>

      {/* ── Panels ── */}
      <nav className="flex border-b border-border text-sm">
        {(
          [
            ["grid", "Grid"],
            // Next to the Grid on purpose: the estimate is what the grid is planned
            // against. The badge counts every line — nested and excluded ones included —
            // so an estimate that exists is visible without opening the tab.
            ["estimates", "Estimates", plan.estimatedHoursLines.length],
            // Epic 2. The label carries the origin, so "Modified from a Standard Trio" is
            // visible without opening the tab — which is the point of recording it.
            ["pod", podTabLabel(plan.plannedPod)],
            ["solve", "Solve"],
            // Epic 8. The count is on the label so "has this ever been sent" is answerable
            // without opening the tab — the same reasoning as the Pod tab's shape source.
            [
              "versions",
              plan.frozenVersionCount === undefined || plan.frozenVersionCount === 0
                ? "Versions"
                : `Versions · ${plan.frozenVersionCount}`,
            ],
            // Epic 17. The label carries the state, because a Pending request is the thing
            // blocking the planner and a tab that just says "Approval" hides it.
            ["approval", approvalTabLabel(plan)],
            // Planned vs actual from NOS time entries once the plan is in delivery, and the
            // linked NOS Demand's own guidance and actuals whenever there is one.
            ...(planShowsActuals(plan) || plan.nosDemand != null ? [["actuals", "Actuals"]] : []),
            // RP-34 Concerns and the plan's comments, in one timeline. The count is still
            // unresolved Concerns, so an open one is visible from the grid without opening it.
            ["activity", "Activity", plan.concerns?.unresolved ?? 0],
            ["changes", "Changes"],
          ] as [PanelTab, string, number?][]
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 font-medium transition-colors",
              tab === key
                ? "border-b-2 border-foreground text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
            {count !== undefined && count > 0 && (
              <span
                className={cn(badgeVariants({ variant: "secondary" }), "px-2 tabular-nums")}
                title={
                  key === "activity"
                    ? `${count} unresolved Staffing Concern${count === 1 ? "" : "s"}`
                    : `${count} estimate line${count === 1 ? "" : "s"}`
                }
              >
                {count}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div>
        {tab === "grid" && (
          <div className="space-y-3">
            <PlanVersionBar plan={plan} onOpenVersions={() => setTab("versions")} />
            <PlanWorkingGrid plan={plan} />
          </div>
        )}
        {tab === "pod" && <PlannedPodPanel plan={plan} />}
        {tab === "solve" && <SolvePanel plan={plan} />}
        {tab === "versions" && <PlanVersionsPanel plan={plan} />}
        {tab === "approval" && <PlanApprovalPanel plan={plan} />}
        {tab === "estimates" && <EstimatesPanel plan={plan} />}
        {tab === "actuals" && (
          <div className="flex flex-col gap-6">
            {planShowsActuals(plan) && <PlanActualsPanel planId={plan.id} />}
            {plan.nosDemand != null && <PlanNosDemandSection demand={plan.nosDemand} />}
          </div>
        )}
        {tab === "activity" && <PlanActivityPanel plan={plan} />}
        {tab === "changes" && <PlanChangesPanel plan={plan} />}
      </div>

      <PlanSetupDialog
        plan={plan}
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onChangeDates={() => {
          setSetupOpen(false);
          setDatesOpen(true);
        }}
      />
      <PlanDatesDialog plan={plan} open={datesOpen} onOpenChange={setDatesOpen} />
      <PlanOwnerDialog plan={plan} open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen} />

      {solutionOpen && plan.solutionContext !== undefined && (
        <SolutionDetailDrawer
          id={plan.solutionContext.solutionId}
          onClose={() => {
            setSolutionOpen(false);
            // The Solution bar above the grid reads from the plan payload, so anything
            // changed in the drawer (Constraints, Expectation Level) shows on close.
            invalidatePlans();
          }}
        />
      )}
    </PageContainer>
  );
}

/** RP-6.1: the header counts the lines whose rate is not the card rate. */
